import { useEffect, useRef, useState } from "react";
import { X, UserRound, ArrowLeft } from "lucide-react";
import { deviceRequest } from "./device-session";
import { initializeAnalytics } from "../portal/analytics";
import { registrationMetaContext, trackRegistration } from "../portal/meta";

export type LoginReason = "login" | "trial" | "save" | "purchase";
export type LoginResult = { newRegistration: boolean; registration: { id: string; eventTime: number; sessionId: string } | null };
const reasons = {
  login: ["注册 / 登录", "输入账户名和密码即可注册，无需邮箱或手机号。"],
  trial: ["喜欢这个故事？", "免费注册，保存追剧进度。下次登录还能接着看。"],
  save: ["免费注册，收藏好故事", "登录后收藏到你的账户，以后可以重新登录找回。"],
  purchase: ["先登录，再购买", "购买记录将保存到你的账户，重新登录即可找回。"],
};
export default function AccountLogin({ reason, onClose, onSuccess }: { reason: LoginReason; onClose: () => void; onSuccess: (result: LoginResult) => Promise<void> }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [mode, setMode] = useState<"register" | "login" | "email">("register");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [available, setAvailable] = useState<boolean | null>(null);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [wait, setWait] = useState(0);
  useEffect(() => { dialog.current?.showModal(); const old = document.body.style.overflow; document.body.style.overflow = "hidden";
    let active = true;
    void deviceRequest<{ usernameAvailable: boolean; emailAvailable: boolean }>("account/config").then(v => {
      if (!active) return;
      setAvailable(v.emailAvailable); setUsernameAvailable(v.usernameAvailable);
      if (!v.usernameAvailable && v.emailAvailable) setMode("email");
    }).catch(() => { if (active) setError("暂时无法连接，请关闭后重试。"); });
    return () => { active = false; dialog.current?.close(); document.body.style.overflow = old; }; }, []);
  useEffect(() => { if (!wait) return; const timer = setTimeout(() => setWait(v => Math.max(0, v - 1)), 1000); return () => clearTimeout(timer); }, [wait]);
  async function send() {
    if (busy || wait > 0) return;
    setBusy(true); setError("");
    try { const result = await deviceRequest<{ challengeId: string; resendAfter: number }>("account/email/code", "POST", { email: email.trim() }); setChallenge(result.challengeId); setCode(""); setWait(result.resendAfter); }
    catch (e) { setError(e instanceof Error ? e.message : "验证码未能发出，请稍后重试。"); }
    finally { setBusy(false); }
  }
  async function verify() {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const session = await initializeAnalytics();
      const result = await deviceRequest<LoginResult>("account/email/verify", "POST", { challengeId: challenge, code, sessionId: session?.sessionId || "", meta: registrationMetaContext() });
      trackRegistration(result.registration);
      await onSuccess(result);
    } catch (e) { setError(e instanceof Error ? e.message : "暂时无法登录，请重试。"); setBusy(false); }
  }
  async function submitAccount() {
    if (busy) return;
    setBusy(true); setError("");
    try {
      if (new TextEncoder().encode(password).length > 72) throw new Error("密码最多为 72 字节，请缩短后重试。");
      const session = mode === "register" ? await initializeAnalytics() : null;
      const result = await deviceRequest<LoginResult>(`account/${mode}`, "POST", {
        username: username.trim(), password,
        ...(mode === "register" ? { sessionId: session?.sessionId || "", meta: registrationMetaContext() } : {}),
      });
      setPassword(""); trackRegistration(result.registration); await onSuccess(result);
    } catch (e) { setError(e instanceof Error ? e.message : "暂时无法登录，请重试。"); setBusy(false); }
  }
  function switchMode(next: typeof mode) { setMode(next); setError(""); setPassword(""); }
  return <dialog ref={dialog} className="email-login-dialog" aria-labelledby="email-login-title" onCancel={e => { e.preventDefault(); if (!busy) onClose(); }}>
    <button className="email-login-close" type="button" aria-label="关闭注册登录" disabled={busy} onClick={onClose}><X size={22} /></button>
    <span className="email-login-icon"><UserRound size={26} /></span>
    <p className="account-eyebrow">Yestv 账户</p>
    <h2 id="email-login-title">{reasons[reason][0]}</h2>
    <p>{mode === "email" ? "验证邮箱后即可找回收藏和观看记录。" : reasons[reason][1]}</p>
    {usernameAvailable !== false && <div className="account-auth-modes" role="group" aria-label="账户操作">
      <button type="button" aria-pressed={mode === "register"} disabled={busy} onClick={() => switchMode("register")}>注册账户</button>
      <button type="button" aria-pressed={mode === "login"} disabled={busy} onClick={() => switchMode("login")}>登录账户</button>
    </div>}
    {usernameAvailable === false && available === false && <p className="email-login-warning" role="status">注册登录暂不可用，请稍后重试。</p>}
    {mode !== "email" ? <form onSubmit={e => { e.preventDefault(); void submitAccount(); }}>
      <label>账户名<input type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} required minLength={3} maxLength={32} pattern="[A-Za-z][A-Za-z0-9_]{2,31}" title="3 到 32 位字母、数字或下划线，以字母开头" value={username} disabled={busy} onChange={e => setUsername(e.target.value)} autoFocus aria-describedby="account-username-hint" /></label>
      <p id="account-username-hint" className="email-login-note">3–32 位字母、数字或下划线，以字母开头，不区分大小写。</p>
      <label>密码<input type="password" autoComplete={mode === "register" ? "new-password" : "current-password"} required minLength={8} maxLength={72} value={password} disabled={busy} onChange={e => setPassword(e.target.value)} aria-describedby="account-password-hint" /></label>
      <p id="account-password-hint" className="email-login-note">密码为 8–72 字节，建议使用至少 8 位字母、数字或符号。</p>
      {error && <p className="email-login-error" role="alert">{error}</p>}
      <button className="account-primary" disabled={busy || usernameAvailable !== true}>{busy ? "处理中…" : mode === "register" ? "立即注册" : "登录"}</button>
    </form> : <form onSubmit={e => { e.preventDefault(); void (challenge ? verify() : send()); }}>
      <p>使用邮箱验证码，新邮箱自动注册，已有邮箱登录原账户。</p>
      <label>邮箱地址<input type="email" autoComplete="email" inputMode="email" required maxLength={254} value={email} disabled={busy || !!challenge} onChange={e => setEmail(e.target.value)} autoFocus /></label>
      {challenge && <><p className="email-code-sent">验证码已发送，请检查收件箱和垃圾邮件。10 分钟内有效。</p>
        <label>6 位验证码<input autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" minLength={6} maxLength={6} required value={code} disabled={busy} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} /></label>
        <div className="email-code-actions"><button type="button" disabled={busy} onClick={() => { setChallenge(""); setCode(""); setError(""); }}><ArrowLeft size={14} />修改邮箱</button><button type="button" disabled={busy || wait > 0} onClick={() => void send()}>{wait ? `${wait} 秒后可重发` : "重新发送验证码"}</button></div>
      </>}
      {error && <p className="email-login-error" role="alert">{error}</p>}
      <button className="account-primary" disabled={busy || available !== true || (!challenge && wait > 0)}>{busy ? "处理中…" : challenge ? "验证并继续" : wait > 0 ? `${wait} 秒后可发送` : "发送验证码"}</button>
    </form>}
    {available && mode !== "email" && <button className="email-login-later" type="button" disabled={busy} onClick={() => switchMode("email")}>邮箱验证码登录</button>}
    <p className="email-login-note">{mode !== "email" && "请记住你的账户名和密码。"}同一账户仅保留一个有效登录端；重新登录后，原登录端将退出。</p>
    <button className="email-login-later" disabled={busy} onClick={onClose}>{reason === "trial" ? "稍后注册，继续试看" : "暂不登录，返回浏览"}</button>
  </dialog>;
}
